import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useContext,
} from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Panel } from '@kizen/kds/Tile';
import DashletError from 'pages/Dashboard/components/DashletError';
import { useTranslation } from 'react-i18next';
import { toastVariant, useToast } from 'components/ToastProvider';
import {
  updatePageConfig,
  getCustomObjectConfig,
} from 'store/recordDetailPage/record.redux';

import FieldService from 'services/FieldService';

import useAsync from 'react-use/lib/useAsync';

import styled from '@emotion/styled';
import KizenTypography from 'app/kizentypo';
import { useBreakpoint, layers } from 'app/spacing';
import { PageSearchInput } from 'components/Layout/PageToolbar';
import useCustomObjectDetails from 'components/Wizards/shared/hooks/useCustomObjectDetails';
import { ConfirmationModal } from 'components/Charts/ScheduledActivities/ConfirmationModal';
import PipelineModal from 'pages/Common/WorkflowPipeline/PipelineModal';
import { monitoringExceptionHelper } from 'instrumentation/helpers';

import { head } from 'utility/snippets';

import { Table } from './Table';
import { TableCard } from './TableCard';
import { RelatedObjectButtonSelector } from './RelatedObjectButtonSelector';
import { HiddenWidth } from './HiddenWidth';
import { RelatedMenu } from './Menu';

import { Header, RelatedObjectSelect, TableWrapper } from './styles';
import { SizedCell } from 'components/Kizen/Table';

import { getCustomRecordsColumns } from './custom/columns';
import { getContactRecordsColumns } from './contact/columns';

import { useErrorByKey } from 'hooks/useErrors';
import { useWindowSize } from 'react-use';
import { useSelector, useDispatch } from 'react-redux';
import { getCustomObjectAccess } from 'store/authentication/selectors';
import { camelToSnakeCaseKeys } from 'services/helpers';
import { INVALID_FIELD_PERMISSION_MESSAGE } from './constants';
import { PluginContext } from 'ts-components/Plugins/context';
import { getEntityRecordsSearchQuery } from './getEntityRecordsSearchQuery';
import { useRecordDetailCustomScript } from '@kizenapps/engine/react';
import IconButtonAbsoluteMenu from '__components/Tables/Big/IconButtonAbsoluteMenu';
import { colorsButton } from '__app/colors';
import Icon from '__components/Kizen/Icon';
import { findMatchingAction } from '@kizenapps/engine/util';

const StyledStickyHeadCell = styled(SizedCell)`
  position: sticky;
  right: 0;
  z-index: ${layers.content(0, 2)};
  background: var(--color-background-white);
`;

const ControlsCell = styled((props) => {
  const { t } = useTranslation();
  const { options, onSelectAction } = props;

  const actionMenu = (
    <IconButtonAbsoluteMenu
      sizing="dense"
      title={t('Actions')}
      position="right"
      color={colorsButton.iconGray}
      options={options}
      onChange={onSelectAction}
    >
      <Icon icon="three-dot" />
      <div className="right-fade"></div>
    </IconButtonAbsoluteMenu>
  );

  return <SizedCell {...props}>{actionMenu}</SizedCell>;
})`
  position: sticky;
  right: 0;
  z-index: ${layers.content(0, 2)};

  & .right-fade {
    height: 100%;
    position: absolute;
    right: 0px;
    width: 60px;
    z-index: -1;
    background: linear-gradient(
      to right,
      color-mix(in srgb, var(--color-background-white) 0%, transparent),
      var(--color-background-white),
      var(--color-background-white)
    );
    transform: translateX(8px);
  }
`;

const hasValidAction = (action) => {
  return Boolean(action?.jsAction);
};

const EntityActionsCell = ({ head: isHead, data, entityActions, onAction }) => {
  const options = useMemo(() => {
    return (entityActions || []).map((action) => ({
      value: action.id,
      label: action.label || 'Action',
    }));
  }, [entityActions]);

  const handleSelectAction = useCallback(
    (selectedActionOption) => {
      const action = (entityActions || []).find(
        (a) => a.id === selectedActionOption.value
      );

      if (action) {
        onAction(action, data?.id);
      }
    },
    [entityActions, onAction, data]
  );

  if (isHead) {
    return <StyledStickyHeadCell head />;
  }

  return (
    <ControlsCell
      options={options}
      data={data}
      onSelectAction={handleSelectAction}
      menuType="observable"
    />
  );
};

const DEFAULT_TABLE_HEIGHT = 294; // 5 rows plus header
const OBJECT_SELECTOR_WIDTH_THRESOLD = 250;

const isContactObject = (fetchUrl) => fetchUrl === 'client';

const isFieldPermissionError = (errors) => {
  return errors?.some((error) =>
    error?.field?.includes(INVALID_FIELD_PERMISSION_MESSAGE)
  );
};

const defaultRelatedObjects = {
  filterLookup: null,
  relatedObjectOptions: [],
  models: [],
  forbiddenModels: [],
};

const toOptions = ({ id, displayName, icon, color, name, objectType }) => {
  return {
    value: id,
    label: displayName,
    icon,
    color,
    name,
    objectType,
  };
};

const getCustom = async (id, objectType, uniqueId) => {
  const [model, fields] = await Promise.all([
    FieldService.getModel({ id }),
    FieldService.getFields({
      for: { id, objectType, objectClass: 'custom_objects' },
    }),
  ]);
  return { model: { ...model, uniqueId }, fields };
};

const getClient = async (id, uniqueId) => {
  const [model, fields] = await Promise.all([
    FieldService.getModel({ id }),
    FieldService.getFields({
      for: 'contacts',
    }),
  ]);
  return { model: { ...model, uniqueId }, fields };
};

const getFilterLookup = async (
  objectModel,
  objects,
  entityId,
  meta,
  modelIdLookup,
  fieldsMap
) => {
  try {
    const objectId = objectModel.id;
    const modelAndFieldRequests = [];

    // look up of relations that are active on the current object
    const currentRelations = (objectModel.relatedObjects || []).reduce(
      (accumulatedRelations, { relatedObject }) => ({
        ...accumulatedRelations,
        [relatedObject]: true,
      }),
      {}
    );

    for (const { id, objectType, fetchUrl } of objects) {
      const modelId = modelIdLookup(id);

      if (isContactObject(fetchUrl)) {
        modelAndFieldRequests.push(
          getClient(modelId, id).then(
            (result) => ({ success: true, result }),
            (error) => ({ success: false, error, modelId, id })
          )
        );
      } else {
        modelAndFieldRequests.push(
          getCustom(modelId, objectType, id).then(
            (result) => ({ success: true, result }),
            (error) => ({ success: false, error, modelId, id })
          )
        );
      }
    }

    const modelsWithStatus = await Promise.all(modelAndFieldRequests);

    // Filter out failed requests
    const successfulModels = modelsWithStatus
      .filter(({ success }) => success)
      .map(({ result }) => {
        return {
          ...result,
          reverseFieldsById: result?.fields?.reduce((acc, field) => {
            acc[field.id] = field;
            return acc;
          }, {}),
        };
      });

    // Collect 403 errors
    const forbiddenModels = modelsWithStatus
      .filter(({ error }) => error?.response?.status === 403)
      .map(({ error, modelId, id }) => ({ error, modelId, id }));

    const filterLookup = successfulModels.reduce(
      (
        accumulated,
        { model = {}, fields = {}, reverseFieldsById = {} } = {}
      ) => {
        const { id, relatedObjects = [], isCustom, uniqueId } = model ?? {};
        const relationshipFields = meta?.relationshipFields?.[uniqueId] ?? [];

        const filters = relatedObjects
          .filter(({ fieldId }) =>
            relationshipFields.length
              ? relationshipFields.some(
                  ({ relation }) => fieldId === relation?.relatedField
                )
              : fieldsMap[fieldId] || reverseFieldsById[fieldId]
          )
          .reduce(
            (accumulatedfilters, { relatedObject = {}, fieldId } = {}) => {
              if (relatedObject === objectId) {
                accumulatedfilters = [
                  ...accumulatedfilters,
                  {
                    type: 'fields_v2',
                    subtype: 'custom',
                    value: [entityId],
                    field: `custom::${fieldId}`,
                    condition: 'contains',
                  },
                ];
              }

              return accumulatedfilters;
            },
            []
          );

        const fieldIds = (
          meta?.fields?.[uniqueId] ??
          meta?.fields?.[id] ??
          []
        ).reduce((acc, field) => {
          if (field.id === 'fullName') {
            const first = fields.find((q) => q.name === 'first_name');
            const last = fields.find((q) => q.name === 'last_name');
            if (first) {
              acc.push(first.id);
            }
            if (last) {
              acc.push(last.id);
            }
          } else {
            acc.push(field.id);
          }
          return acc;
        }, []);

        return {
          ...accumulated,
          [uniqueId]: {
            filters,
            isCustom,
            model,
            fields,
            activeRelationship: currentRelations[id] || false,
            fieldIds,
            reverseFieldsById,
          },
        };
      },
      {}
    );

    return {
      models: successfulModels,
      filterLookup,
      forbiddenModels,
    };
  } catch (error) {
    return { error };
  }
};

const resetProps = {
  page: 1,
  search: '',
};

// look up names for default fields
const fixupNameForDefaultFields = (filterLookup, key, id, modelIdLookup) => {
  if (!filterLookup) {
    return id;
  }
  const field = filterLookup[key]?.fields.find((f) => f.id === id);

  return field ? (field.isDefault ? field.name : id) : id;
};

const useRelatedObjectFields = ({
  objectModel,
  entityId,
  t,
  meta,
  modelIdLookup,
  adornments,
  fields: rawFields,
}) => {
  const [showToast] = useToast();
  const dispatch = useDispatch();
  const displayErrorToastRef = useRef(true);
  const [state, setState] = useState({
    search: '',
    relatedObject: null,
    relatedObjectModel: null,
    relatedObjectFields: null,
    relatedObjectFieldIds: [],
    page: 1,
    pageSize: 100, // relationship sorting query is expensive so we want to limit the amount of calls.
  });

  const {
    search,
    relatedObject,
    pageSize,
    relatedObjectModel,
    relatedObjectFields,
    relatedObjectFieldIds,
  } = state;

  const {
    objects = null,
    fields,
    collapse,
    tableHeight = DEFAULT_TABLE_HEIGHT,
  } = meta;

  const objectAccess = useSelector((s) => {
    return (
      s.authentication.access?.custom_objects?.custom_object_entities ?? {}
    );
  });
  const { detailPageConfig = {} } = useSelector(
    getCustomObjectConfig(objectModel.id)
  );
  // filter out any objects we don't have access to
  const { relatedObjects, fieldAccess } = useMemo(() => {
    const filteredObjects = (objects ?? []).filter((o) => {
      const modelId = modelIdLookup(o.id);
      return objectAccess[modelId]?.enabled || isContactObject(o.fetchUrl);
    });
    const fieldAccess = filteredObjects.reduce(
      (acc, { id }) => ({ ...acc, [id]: { enabled: true } }),
      {}
    );
    return { relatedObjects: filteredObjects, fieldAccess };
  }, [objects, objectAccess, modelIdLookup]);

  const selectedRelatedObject = useMemo(
    () => relatedObjects.find(({ id }) => relatedObject?.value === id),
    [relatedObjects, relatedObject]
  );

  const fieldIdMap = useMemo(() => {
    return rawFields?.reduce((acc, category) => {
      category.fields.forEach((field) => {
        if (
          field?.relation?.relatedField &&
          fieldAccess[field?.relation?.relatedField]
        ) {
          acc[field.relation.relatedField] = field;
        }
      });
      return acc;
    }, {});
  }, [rawFields, fieldAccess]);

  const dataForUseAsyncHook = useRef({ relatedObjects, fieldIdMap });
  dataForUseAsyncHook.current.relatedObjects = relatedObjects;
  dataForUseAsyncHook.current.fieldIdMap = fieldIdMap;

  // use this to calculate the filter options and load the filterloook up
  const {
    value: {
      filterLookup,
      relatedObjectOptions,
      models,
      forbiddenModels,
    } = defaultRelatedObjects,
    loading: modelsLoading,
  } = useAsync(async () => {
    if (objectModel.id && entityId) {
      try {
        const { filterLookup, models, forbiddenModels } = await getFilterLookup(
          objectModel,
          dataForUseAsyncHook.current.relatedObjects,
          entityId,
          meta,
          modelIdLookup,
          dataForUseAsyncHook.current.fieldIdMap
        );

        const availableModelsById = models?.reduce((acc, { model } = {}) => {
          acc[model.id] = true;
          return acc;
        }, {});

        const options = relatedObjects
          .filter(({ id, originalId }) => {
            return availableModelsById?.[originalId] && filterLookup?.[id];
          })
          .map(toOptions);

        if (
          options.length != relatedObjects.length &&
          displayErrorToastRef.current
        ) {
          displayErrorToastRef.current = false;
          showToast({
            message: `${t('There was an error loading some of the Related Objects.')}`,
            variant: toastVariant.FAILURE,
          });
        }

        return {
          filterLookup,
          relatedObjectOptions: options,
          models,
          forbiddenModels,
        };
      } catch (error) {
        if (error?.response?.status !== 403 && displayErrorToastRef.current) {
          displayErrorToastRef.current = false;
          showToast({
            message: `${t('There was an error loading some of the Related Objects.')}`,
            variant: toastVariant.FAILURE,
          });
          return {
            filterLookup: {},
            options: [],
            models: {},
            forbiddenModels: [],
          };
        }
      }
    }

    return defaultRelatedObjects;
  }, [objectModel.id, entityId]);

  const { data: relatedObjectDetails, isLoading: relatedObjectDetailsLoading } =
    useCustomObjectDetails({
      objectId: relatedObjectModel?.id,
      enabled: true,
    });

  // used to filter out deleted fields or fields we don't have access to
  const existingRelationFieldsById = useMemo(
    () =>
      relatedObjectDetails?.fields?.reduce((acc, field) => {
        if (field.name === 'first_name') {
          acc['firstName'] = field;
        } else if (field.name === 'last_name') {
          acc['lastName'] = field;
        }
        return { ...acc, [field.id]: field };
      }, {}) ?? {},
    [relatedObjectDetails]
  );

  // we want to get rid of object fields we don't have access to also
  const relatedObjectsColumnSettings = useMemo(
    () =>
      Object.entries(fields).reduce((acc, [key, fields]) => {
        return fieldAccess[key]?.enabled
          ? {
              ...acc,
              [key]: fields.map(
                (
                  { id, displayName, label, width, enableLink = true },
                  order
                ) => ({
                  order,
                  id: fixupNameForDefaultFields(filterLookup, key, id),
                  label: label || displayName,
                  width,
                  enableLink,
                })
              ),
            }
          : acc;
      }, {}),
    [filterLookup, fields, fieldAccess]
  );

  // SET STATE
  //====================================================
  const setSearch = useCallback((value) => {
    setState((lastState) => ({
      ...lastState,
      search: value,
    }));
  }, []);

  // used to remember on reload
  const relatedObjectRef = useRef(relatedObject);

  // once the options have loaded this is safe
  const setRelatedObject = useCallback(
    (relatedObject) => {
      const lookupId = relatedObject?.value;
      if (relatedObject && filterLookup && filterLookup[lookupId]) {
        relatedObjectRef.current = relatedObject;

        const relatedObjectModel = filterLookup[lookupId].model;
        const relatedObjectFields = filterLookup[lookupId].fields;
        const relatedObjectFieldIds = filterLookup[lookupId].fieldIds;

        setState((lastState) => ({
          ...lastState,
          relatedObject,
          relatedObjectModel,
          relatedObjectFields,
          relatedObjectFieldIds,
          ...resetProps,
        }));
      }
    },
    [filterLookup]
  );

  const setOrdering = useCallback(
    (updatedOrdering) => {
      const blockDetails = detailPageConfig?.[entityId] ?? {};
      dispatch(
        updatePageConfig({
          customObjectId: objectModel.id,
          blockId: entityId,
          detailPageConfig: {
            ...detailPageConfig,
            [entityId]: {
              ...blockDetails,
              ordering: {
                ...blockDetails.ordering,
                [relatedObject?.value]: updatedOrdering,
              },
            },
          },
        })
      );
    },
    [entityId, relatedObject, objectModel, detailPageConfig, dispatch]
  );

  // handle setting default related option once the models have loaded
  useEffect(() => {
    if (models && relatedObjectOptions) {
      const savedRelatedObject = relatedObjectRef.current;
      if (savedRelatedObject && filterLookup?.[savedRelatedObject.value]) {
        setRelatedObject(savedRelatedObject);
      } else {
        setRelatedObject(head(relatedObjectOptions));
      }
    }
  }, [models, relatedObjectOptions, filterLookup, setRelatedObject]);

  // once everything is fetched figure out the columns
  const columns = useMemo(() => {
    if (
      relatedObjectModel &&
      relatedObjectFields &&
      relatedObjectsColumnSettings &&
      relatedObject
    ) {
      return relatedObjectModel.isCustom
        ? getCustomRecordsColumns({
            model: relatedObjectModel,
            fields: relatedObjectFields,
            adornments,
            columnSettings:
              relatedObjectsColumnSettings[relatedObject?.value] || [],
            onSelectAction: () => {},
            onSubmitRecord: () => {},
            handleUpdateFieldOption: () => {},
            handleUpdateTableRecords: () => {},
            preReleaseFeatures: true,
            t,
          })
        : getContactRecordsColumns({
            model: relatedObjectModel,
            fields: relatedObjectFields,
            adornments,
            columnSettings:
              relatedObjectsColumnSettings[relatedObject?.value] || [],
            onSubmitContact: () => {},
            categorizedFields: [],
            handleUpdateFieldOption: () => {},
            handleUpdateTableRecords: () => {},
            serviceToUse: FieldService,
            t,
          });
    }
    return [];
  }, [
    adornments,
    relatedObjectModel,
    relatedObjectFields,
    relatedObjectsColumnSettings,
    relatedObject,
    t,
  ]);

  const filters = useMemo(() => {
    return meta?.filters?.[relatedObject?.value];
  }, [meta, relatedObject]);

  const ordering =
    detailPageConfig[entityId]?.ordering?.[relatedObject?.value] ||
    (isContactObject(selectedRelatedObject?.fetchUrl) ? 'full_name' : 'name');

  return {
    search,
    setSearch,
    relatedObject,
    relatedObjectModel,
    relatedObjectFields,
    relatedObjectFieldIds,
    existingRelationFieldsById,
    relatedObjectDetailsLoading,
    relatedObjectDetails,
    setRelatedObject,
    relatedObjectOptions,
    relatedObjectsColumnSettings,
    models,
    forbiddenModels,
    filterLookup,
    filters,
    modelsLoading,
    columns,
    ordering,
    pageSize,
    setOrdering,
    collapse,
    fieldIdMap,
    tableHeight,
  };
};

export const RelatedObjectFields = ({
  id,
  objectModel,
  clientObject,
  entityId,
  meta,
  fields,
  controller,
  frameless,
  resetAllFields,
  saveFormData,
  fieldState,
  onChangeFieldState,
  handleUpdateRecord,
  handleFieldBlur,
  touchedFormData,
  refetch,
}) => {
  const { customObjectEntities } = useSelector(getCustomObjectAccess);
  const { contacts } = useSelector((s) => s.authentication.access);
  const { dataAdornments, plugins = {} } = useContext(PluginContext);
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const objectSelectorRef = useRef();
  const refWidthFinder = useRef();
  const [buttonWidth, setButtonWidth] = useState(0);
  const isMobile = useBreakpoint();
  const [showToast] = useToast();
  const previousInfiniteQuery = useRef();

  const handleRecordDetailScriptError = useCallback(
    (error) => {
      monitoringExceptionHelper(error);
      showToast({
        variant: toastVariant.FAILURE,
        message:
          error?.message ?? t('An error occurred while executing the script'),
      });
    },
    [showToast, t]
  );

  const [executeScript, internalState] = useRecordDetailCustomScript({
    entityId,
    objectId: objectModel?.id,
    onError: handleRecordDetailScriptError,
  });

  const { pending } = internalState;

  const modelIdLookup = useCallback(
    (key) => (meta?.idDictionary ? meta.idDictionary[key] : key),
    [meta.idDictionary]
  );

  const metaWithCorrectFilters = useMemo(() => {
    return {
      ...meta,
      filters: camelToSnakeCaseKeys(meta.filters),
    };
  }, [meta]);

  const [collapseOveride, setCollapseOveride] = useState(false);
  const [showAddRecordModal, setShowAddRecordModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const {
    search,
    setSearch,
    relatedObject,
    setRelatedObject,
    relatedObjectOptions,
    existingRelationFieldsById,
    relatedObjectDetailsLoading,
    relatedObjectDetails,
    modelsLoading,
    forbiddenModels,
    filterLookup,
    filters,
    columns,
    relatedObjectModel,
    setOrdering,
    ordering,
    pageSize,
    fieldIdMap,
    tableHeight = DEFAULT_TABLE_HEIGHT,
  } = useRelatedObjectFields({
    objectModel,
    entityId,
    meta: metaWithCorrectFilters,
    t,
    modelIdLookup,
    adornments: dataAdornments,
    fields,
  });

  const relationshipFields =
    meta.relationshipFields[relatedObject?.value] ?? [];

  const missingAllRelationshipFields =
    relationshipFields.length > 0 &&
    relationshipFields.every(
      ({ relation }) => !existingRelationFieldsById[relation?.relatedField]
    );

  const {
    filters: additionalFilters = [],
    fieldIds = [],
    activeRelationship = false,
    model: { id: customObjectId = '' } = {},
    reverseFieldsById = {},
  } = (relatedObject && filterLookup?.[relatedObject?.value]) || {};

  // ensure we have access to all fields we pass to query
  const filteredFieldIds = fieldIds.filter(
    (fieldId) => reverseFieldsById[fieldId] || fieldIdMap[fieldId]
  );

  const infiniteQuery = useInfiniteQuery(
    getEntityRecordsSearchQuery({
      recordId: entityId,
      customObjectId,
      additionalFilters,
      blockFilters: filters?.custom_filters,
      fieldIds: filteredFieldIds,
      searchWithinFieldIds: isMobile ? [] : filteredFieldIds,
      search,
      ordering,
      pageSize,
      enabled:
        Boolean(relatedObject) &&
        activeRelationship &&
        additionalFilters.length !== 0,
    })
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    status,
    isLoading,
    isError,
    error,
    refetch: refetchRelatedObjects,
  } = infiniteQuery;

  if (isError && !previousInfiniteQuery.current.isError) {
    monitoringExceptionHelper(error);
    showToast({
      message: t(
        'Unable to load Related information. Please try again or contact Kizen support.'
      ),
      variant: toastVariant.FAILURE,
    });
  }

  previousInfiniteQuery.current = infiniteQuery;

  const filterErrors = useErrorByKey(error?.response?.data?.errors, 'filters');

  const relatedObjectRef = useRef(relatedObject?.value);
  const forceLoading = Boolean(data?.pages?.[0]?.initial);

  if (
    refWidthFinder?.current?.scrollWidth &&
    refWidthFinder?.current?.scrollWidth !== buttonWidth
  ) {
    setButtonWidth(refWidthFinder?.current?.scrollWidth);
  }

  useEffect(() => {
    setCollapseOveride(() =>
      objectSelectorRef?.current &&
      objectSelectorRef?.current?.scrollWidth < OBJECT_SELECTOR_WIDTH_THRESOLD
        ? true
        : relatedObjectOptions?.length > 4 &&
          buttonWidth > objectSelectorRef?.current?.scrollWidth
    );
  }, [relatedObjectOptions, width, buttonWidth]);

  useEffect(() => {
    if (relatedObject?.value !== relatedObjectRef.current || error) {
      setShowAddRecordModal(false);
    }
  }, [relatedObject?.value, error]);

  const handleAddRecord = useCallback(
    (newId) => {
      const scriptApiName =
        meta?.addButtonJsActionByObjectId?.[relatedObject?.value];

      if (scriptApiName) {
        const actionOptions = relatedObjectDetails?.browserJsActions || [];

        const action = findMatchingAction(actionOptions, scriptApiName);

        if (action && action.script) {
          const config =
            plugins[action.pluginApp?.apiName]?.business_config ?? {};

          executeScript(
            action.script,
            { ...config, pluginId: action.pluginApp?.id },
            { plugin_api_name: action.pluginApp?.apiName, script_id: action.id }
          );

          return;
        }
      }

      if (touchedFormData) {
        setShowConfirmationModal(true);
      } else {
        setShowAddRecordModal(true);
      }
    },
    [
      setShowAddRecordModal,
      setShowConfirmationModal,
      touchedFormData,
      meta,
      relatedObject,
      relatedObjectDetails,
      executeScript,
      plugins,
    ]
  );

  const handleEntityAction = useCallback(
    (action, rowEntityId) => {
      if (!hasValidAction(action)) {
        return;
      }

      const jsAction = findMatchingAction(
        relatedObjectDetails?.browserJsActions,
        action.jsAction
      );

      if (jsAction && jsAction.script) {
        const config =
          plugins[jsAction.pluginApp?.apiName]?.business_config ?? {};

        executeScript(
          jsAction.script,
          { ...config, pluginId: jsAction.pluginApp?.id },
          {
            plugin_api_name: jsAction.pluginApp?.apiName,
            script_id: jsAction.id,
          },
          {
            entityId,
            objectId: objectModel?.id,
            actionEntityId: rowEntityId,
            actionObjectId: relatedObjectModel?.id,
          }
        );
      }
    },
    [
      relatedObjectDetails,
      plugins,
      executeScript,
      entityId,
      objectModel,
      relatedObjectModel,
    ]
  );

  const invalidAddRecordAccess = useMemo(() => {
    return (
      Boolean(error) ||
      Boolean(
        forbiddenModels?.find((inaccessableModels) => {
          return inaccessableModels?.id === objectModel?.id;
        })
      ) ||
      customObjectEntities[objectModel?.id]?.canCreate === false ||
      customObjectEntities[modelIdLookup(relatedObject?.value)]?.canCreate ===
        false ||
      (contacts.id === objectModel?.id && !contacts?.canCreate)
    );
  }, [
    forbiddenModels,
    relatedObject,
    modelIdLookup,
    customObjectEntities,
    objectModel,
    contacts,
    error,
  ]);

  const entityActionsColumn = useMemo(() => {
    const entityActions =
      meta?.entityActionsByObjectId?.[relatedObject?.value] || [];
    const availableActions = relatedObjectDetails?.browserJsActions || [];

    const filteredActions = entityActions.filter((action) => {
      if (!hasValidAction(action)) {
        return false;
      }

      const jsAction = findMatchingAction(availableActions, action.jsAction);

      return Boolean(jsAction);
    });

    if (!filteredActions.length || invalidAddRecordAccess) {
      return null;
    }

    return {
      id: 'entity-actions',
      headCell: <EntityActionsCell head entityActions={filteredActions} />,
      cell: (
        <EntityActionsCell
          entityActions={filteredActions}
          onAction={handleEntityAction}
        />
      ),
    };
  }, [
    meta,
    relatedObject,
    relatedObjectDetails,
    handleEntityAction,
    invalidAddRecordAccess,
  ]);

  const controllerElement = useMemo(() => {
    return controller?.({
      options: relatedObjectOptions,
      value: relatedObject,
      onChange: setRelatedObject,
      search,
      setSearch,
      loading: isFetchingNextPage || isFetching,
      handleAddButtonClick: handleAddRecord,
      invalidAddRecordAccess: invalidAddRecordAccess,
      disableAddButton: pending,
    });
  }, [
    invalidAddRecordAccess,
    controller,
    relatedObjectOptions,
    relatedObject,
    setRelatedObject,
    search,
    setSearch,
    isFetchingNextPage,
    isFetching,
    handleAddRecord,
    pending,
  ]);

  const handleUpdate = useCallback(async () => {
    await saveFormData();
    setShowConfirmationModal(false);
    setShowAddRecordModal(true);
  }, [saveFormData, setShowConfirmationModal, setShowAddRecordModal]);

  const handleDismiss = useCallback(() => {
    resetAllFields();
    setShowConfirmationModal(false);
  }, [setShowConfirmationModal, resetAllFields]);

  const pipelineOptions = useMemo(() => {
    return relatedObject?.value || relatedObject?.children
      ? [
          {
            label: relatedObject?.children
              ? relatedObject?.children
              : relatedObject?.label,
            value: modelIdLookup(relatedObject?.value),
            id: modelIdLookup(relatedObject?.value),
          },
        ]
      : [];
  }, [relatedObject, modelIdLookup]);

  const header = controllerElement ? (
    controllerElement
  ) : (
    <>
      <HiddenWidth ref={refWidthFinder} showContent={buttonWidth === 0}>
        <RelatedObjectButtonSelector
          options={relatedObjectOptions}
          value={relatedObject}
          onChange={setRelatedObject}
          noOptionsMessage={t('No Related Objects')}
        />
      </HiddenWidth>
      <Header>
        <div className="rof--related">
          <KizenTypography as="h4" type="subheader">
            {t('Related')}
          </KizenTypography>
        </div>
        <div
          className={`rof--object-selector${
            !collapseOveride ? ' no-overflow' : ''
          }`}
          ref={objectSelectorRef}
        >
          {collapseOveride ? (
            <RelatedObjectSelect
              label={null}
              placeholder={t('Choose Object')}
              options={relatedObjectOptions}
              value={relatedObject}
              onChange={setRelatedObject}
              noOptionsMessage={t('No Related Objects')}
              components={{ Menu: RelatedMenu }}
              dataQa="related-object-select"
            />
          ) : (
            <RelatedObjectButtonSelector
              options={relatedObjectOptions}
              value={relatedObject}
              onChange={setRelatedObject}
              noOptionsMessage={t('No Related Objects')}
            />
          )}
        </div>
        <div className="rof--search">
          <PageSearchInput
            placeholder={t('Search')}
            value={search}
            onChange={setSearch}
            loading={isFetchingNextPage || isFetching}
          />
        </div>
      </Header>
    </>
  );

  const hasNoPermission = !modelsLoading && forceLoading && !relatedObject;
  const isInitialFetching = isFetching && !isFetchingNextPage;
  const additonalLoadingState =
    (modelsLoading ||
      isLoading ||
      forceLoading ||
      isInitialFetching ||
      relatedObjectDetailsLoading) &&
    !hasNoPermission;

  const errorMessage = useMemo(() => {
    if (additonalLoadingState) {
      return false;
    } else if (
      filterErrors?.length > 0 &&
      !isFieldPermissionError(filterErrors)
    ) {
      return t(
        'This tab contains an invalid filter. Please contact your administrator.'
      );
    } else if (
      missingAllRelationshipFields ||
      isFieldPermissionError(filterErrors)
    ) {
      return t(
        'This tab contains a configuration error. Please contact your administrator.'
      );
    }
    return false;
  }, [filterErrors, missingAllRelationshipFields, additonalLoadingState, t]);

  const blockError = errorMessage ? (
    <TableWrapper frameless={frameless}>
      <Panel padding={[0, 20, 0, 20]}>
        <DashletError override={errorMessage} center />
      </Panel>
    </TableWrapper>
  ) : null;

  const body = (
    <>
      {header}
      {blockError ? (
        blockError
      ) : (
        <>
          <TableWrapper frameless={frameless}>
            <Table
              key={`${relatedObject?.value}`}
              columns={
                entityActionsColumn
                  ? [...columns, entityActionsColumn]
                  : columns
              }
              ordering={ordering}
              setOrdering={setOrdering}
              dataForTheTable={data}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              status={status}
              isFetching={isFetching}
              tableHeight={tableHeight}
              frameless={frameless}
              loading={additonalLoadingState}
              centerLoader
            />
          </TableWrapper>
          <PipelineModal
            id={id}
            objectId={objectModel.id}
            entityId={entityId}
            clientObject={clientObject}
            pipelineOptions={pipelineOptions}
            show={showAddRecordModal && !error}
            setShowModal={setShowAddRecordModal}
            onAdd={refetchRelatedObjects}
            relationshipFields={{}}
            fieldState={fieldState}
            onChangeFieldState={onChangeFieldState}
            handleUpdateRecord={handleUpdateRecord}
            handleFieldBlur={handleFieldBlur}
            refetch={refetch}
            entitiyLabel={t('Object')}
            entityPlaceHolder={t('Choose Object')}
          />
          <ConfirmationModal
            show={showConfirmationModal}
            onConfirm={handleUpdate}
            onHide={() => setShowConfirmationModal(false)}
            additionalButtonText={t('Discard Changes')}
            additionalButtonColor="red"
            onAdditionalConfirm={handleDismiss}
            actionBtnColor="green"
            buttonText={t('Save')}
          >
            {t(
              'The record has unsaved changes. Would you like to save them or discard them before creating the new record?'
            )}
          </ConfirmationModal>
        </>
      )}
    </>
  );

  return frameless ? (
    body
  ) : (
    <TableCard tableHeight={tableHeight}>{body}</TableCard>
  );
};
